// Pure helpers for the Events directory + per-event pages (no I/O, safe on client + server).
//
// Events have no stored slug/path in the dump: the URL is DERIVED from the title
// (stable as long as the title is stable). Region is derived from the free-text
// `location`. The "speaking line" is derived from the description rich text — the
// editors already write "Featured speaker: …" / "keynote" paragraphs; we surface them
// on cards instead of hiding them inside the modal.

import type { Event, Tag } from '@/payload-types'

export type EventRegion = 'Europe' | 'North America' | 'Asia' | 'Other'

export type DirectoryEvent = Event & {
  slug: string
  path: string
  region: EventRegion
  speakingLine: string | null
  edition: string | null
  excerpt: string
  tagTitles: string[]
  websiteUrl: string | null
  organizerHref: string | null
}

export const slugifyTitle = (title: string): string =>
  title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/&/g, ' and ')
    .replace(/@/g, ' at ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Deterministic, collision-free slugs for a list of events (collisions get `-yyyy-mm`). */
export const assignEventSlugs = (events: Event[]): Map<string, string> => {
  const counts = new Map<string, number>()
  for (const ev of events) {
    const s = slugifyTitle(ev.title)
    counts.set(s, (counts.get(s) ?? 0) + 1)
  }
  const out = new Map<string, string>()
  for (const ev of events) {
    let s = slugifyTitle(ev.title)
    if ((counts.get(s) ?? 0) > 1 && ev.startDate) s = `${s}-${ev.startDate.slice(0, 7)}`
    out.set(ev.id, s)
  }
  return out
}

const EUROPE =
  /\b(germany|deutschland|austria|switzerland|schweiz|uk|united kingdom|england|france|italy|spain|netherlands|belgium|sweden|norway|denmark|finland|poland|czech|hungary|portugal|ireland|berlin|frankfurt|stuttgart|hamburg|mainz|aachen|hardheim|munich|münchen|basel|zurich|graz|schladming|valencia|bologna|lille|birmingham|silverstone|london|paris|milan|vienna|wien)\b/i
const NORTH_AMERICA =
  /\b(usa|u\.s\.|us|united states|canada|mexico|na|detroit|chicago|orlando|nashville|charlotte|tucson|stanford|las vegas|los angeles|atlanta|lemont|washington|boston|houston|dallas|toronto|montreal|vancouver|illinois|michigan|georgia|nevada|florida|tennessee|arizona|california|texas)\b/i
const ASIA =
  /\b(china|shanghai|beijing|shenzhen|japan|tokyo|korea|seoul|singapore|india|taiwan|hong kong|vietnam|thailand|indonesia|malaysia)\b/i

export const regionFromLocation = (location?: string | null): EventRegion => {
  const l = (location ?? '').trim()
  if (!l) return 'Other'
  if (ASIA.test(l)) return 'Asia'
  if (EUROPE.test(l)) return 'Europe'
  if (NORTH_AMERICA.test(l)) return 'North America'
  return 'Other'
}

type LexicalNode = { type?: string; text?: string; children?: LexicalNode[]; format?: number }

/** Paragraph-level plain text of a Lexical rich-text root. */
export const richTextParagraphs = (description: Event['description']): string[] => {
  const root = (description as { root?: LexicalNode } | null | undefined)?.root
  if (!root?.children) return []
  const textOf = (n: LexicalNode): string =>
    n.text ?? (n.children ?? []).map(textOf).join('')
  const out: string[] = []
  const walk = (n: LexicalNode) => {
    if (n.type === 'paragraph' || n.type === 'listitem' || n.type === 'heading') {
      const t = textOf(n).replace(/\s+/g, ' ').trim()
      if (t) out.push(t)
      return
    }
    ;(n.children ?? []).forEach(walk)
  }
  walk(root)
  return out
}

/** First substantive paragraph, trimmed to ~`max` chars on a word boundary. */
export const excerptOf = (description: Event['description'], max = 180): string => {
  const first = richTextParagraphs(description).find(
    (p) => !/^(good to know|featured speaker|key \d{4} focus)/i.test(p),
  )
  if (!first) return ''
  if (first.length <= max) return first
  const cut = first.slice(0, max)
  return cut.slice(0, Math.max(cut.lastIndexOf(' '), 60)).replace(/[,;:]$/, '') + '…'
}

/**
 * A line worth surfacing on the card: the editors' own "Featured speaker:" / keynote
 * paragraph, or the long recommendation when it announces a talk. Never invented.
 */
export const speakingLineOf = (ev: Event): string | null => {
  const recommenders = (ev.recommendedBy ?? [])
    .map((r) => (typeof r === 'object' && r ? (r as { name?: string }).name : null))
    .filter((n): n is string => Boolean(n))
  const surnames = recommenders.map((n) => n.trim().split(/\s+/).pop() ?? n)

  // 1. The editors' own "Featured speaker: … / … will present …" paragraph naming an expert.
  const paras = richTextParagraphs(ev.description)
  const hit = paras.find(
    (p) =>
      /\b(keynote|speak(ing|er|s)?|present(s|ing)?|panel|talk)\b/i.test(p) &&
      (/wright/i.test(p) || surnames.some((s) => new RegExp(`\\b${s}\\b`, 'i').test(p))),
  )
  if (hit) return hit.replace(/^featured speaker:\s*/i, '').trim()

  // 2. The recommender's own words ("I'm speaking …", "I'll be giving the keynote …").
  const quote = [ev.longRecommendation, ev.recommendedWhy].find(
    (q) => q && /\bI(?:['’]m|['’]ll| am| will)\s+(?:be\s+)?(?:speaking|giving|presenting|hosting|moderating|on a panel)/i.test(q),
  )
  if (quote && recommenders[0]) {
    const name = recommenders[0]
    const titled = quote.match(/[“"]([^”"]{8,120})[”"]/)
    if (/keynote/i.test(quote)) return titled ? `${name} gives the keynote: “${titled[1]}”` : `${name} gives the keynote.`
    if (/panel/i.test(quote)) return `${name} is on a panel here.`
    return titled ? `${name} is speaking: “${titled[1]}”` : `${name} is speaking here.`
  }
  return null
}

const ORDINAL_WORDS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
  eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15, twentieth: 20, twentyfifth: 25, thirtieth: 30,
}
const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'] as const
  const v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`
}

/**
 * "32nd edition" / "First edition" / "30+ years", derived only from what the data already says:
 * an ordinal in the title ("7th Future Battery Forum"), an ordinal + edition/annual in the
 * description ("fifth annual", "32nd edition"), or a "for over 30 years" phrase. Never invented.
 */
export const editionOf = (ev: Event): string | null => {
  // Explicit number researched per event (2026-09-15) wins; stored on the event doc as
  // `editionNumber` (not a CMS field; the static dump is the CMS now).
  const explicit = (ev as { editionNumber?: number | null }).editionNumber
  if (typeof explicit === 'number' && explicit > 0) return editionLabel(explicit)
  const title = ev.title
  const paras = richTextParagraphs(ev.description).join(' ')
  const fromTitle = title.match(/\b(\d{1,3})(?:st|nd|rd|th)\b/i)
  if (fromTitle) return editionLabel(Number(fromTitle[1]))
  const fromDesc = paras.match(/\b(\d{1,3})(?:st|nd|rd|th)\s+(?:annual\s+|international\s+)?(?:edition|annual|congress|conference|year|time|meeting|forum|summit|workshop|show|expo)\b/i)
  if (fromDesc) return editionLabel(Number(fromDesc[1]))
  const fromWord = paras.match(/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|twentieth|thirtieth)\s+(?:annual\s+|international\s+)?(?:edition|annual|congress|conference|year|time|meeting|forum|summit|workshop|show|expo)\b/i)
  if (fromWord) return editionLabel(ORDINAL_WORDS[fromWord[1]!.toLowerCase()] ?? 0)
  const years = paras.match(/\b(?:for\s+)?(?:over|more than|nearly|almost)\s+(\d{2})\s+(?:consecutive\s+)?years\b/i) ?? paras.match(/\bfor\s+(\d{2})\s+(?:consecutive\s+)?years\b/i)
  if (years) return `${years[1]}+ years`
  if (/\b(inaugural|first[- ]ever|brand[- ]new|launch(?:ing|es)? (?:in|this) \d{4}|debut)\b/i.test(paras) || /\binaugural\b/i.test(title)) return 'First edition'
  return null
}
const editionLabel = (n: number): string | null => (n <= 0 ? null : n === 1 ? 'First edition' : `${ordinal(n)} edition`)

export const tagTitlesOf = (ev: Event): string[] =>
  (ev.tags ?? [])
    .map((t) => (typeof t === 'object' && t ? (t as Tag).title : null))
    .filter((t): t is string => Boolean(t))

export const websiteUrlOf = (ev: Event): string | null => {
  const url = ev.link?.link?.url
  return ev.showCTAButton !== false && url ? url : null
}

export const toDirectoryEvents = (
  events: Event[],
  opts: { publishedVendorPaths?: Set<string> } = {},
): DirectoryEvent[] => {
  const slugs = assignEventSlugs(events)
  return events.map((ev) => {
    const slug = slugs.get(ev.id) ?? slugifyTitle(ev.title)
    return {
      ...ev,
      slug,
      path: `/events/${slug}`,
      region: regionFromLocation(ev.location),
      speakingLine: speakingLineOf(ev),
      edition: editionOf(ev),
      excerpt: excerptOf(ev.description),
      tagTitles: tagTitlesOf(ev),
      websiteUrl: websiteUrlOf(ev),
      organizerHref: organizerHrefOf(ev, opts.publishedVendorPaths),
    }
  })
}

/** Day-precision "still relevant" test: multi-day events stay until their end date has passed. */
export const isUpcoming = (ev: Pick<Event, 'startDate' | 'endDate'>, now = new Date()): boolean => {
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const last = new Date(ev.endDate || ev.startDate)
  last.setHours(23, 59, 59, 999)
  return last >= today
}

export const monthKeyOf = (iso: string): string => iso.slice(0, 7) // yyyy-mm

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const monthLabel = (key: string, style: 'long' | 'short' = 'long'): string => {
  const [y, m] = key.split('-')
  const name = MONTHS[Number(m) - 1] ?? ''
  return style === 'long' ? `${name} ${y}` : `${name.slice(0, 3)} ${y?.slice(2)}`
}

export type MonthGroup = { key: string; label: string; events: DirectoryEvent[] }

export const groupByMonth = (events: DirectoryEvent[]): MonthGroup[] => {
  const map = new Map<string, DirectoryEvent[]>()
  for (const ev of events) {
    const k = monthKeyOf(ev.startDate)
    if (!map.has(k)) map.set(k, [])
    map.get(k)!.push(ev)
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, evs]) => ({ key, label: monthLabel(key), events: evs }))
}

/** Rolling window of month keys starting at `from` (inclusive). */
export const monthWindow = (from: Date, count = 12): string[] => {
  const out: string[] = []
  const d = new Date(from.getFullYear(), from.getMonth(), 1)
  for (let i = 0; i < count; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    d.setMonth(d.getMonth() + 1)
  }
  return out
}

/** "8–10 Sep 2027" / "28–29 Oct 2026" / "21 Sep 2026" / "30 Nov – 3 Dec 2026" */
export const formatDateRange = (start: string, end?: string | null): string => {
  const s = new Date(start)
  const e = end ? new Date(end) : null
  const day = (d: Date) => d.getUTCDate()
  const mon = (d: Date) => MONTHS[d.getUTCMonth()]!.slice(0, 3)
  const yr = (d: Date) => d.getUTCFullYear()
  if (!e || e.getTime() === s.getTime() || e < s) return `${day(s)} ${mon(s)} ${yr(s)}`
  if (yr(s) !== yr(e)) return `${day(s)} ${mon(s)} ${yr(s)} – ${day(e)} ${mon(e)} ${yr(e)}`
  if (mon(s) !== mon(e)) return `${day(s)} ${mon(s)} – ${day(e)} ${mon(e)} ${yr(e)}`
  return `${day(s)}–${day(e)} ${mon(s)} ${yr(s)}`
}

const US_STATES = new Set(
  'alabama,alaska,arizona,arkansas,california,colorado,connecticut,delaware,florida,georgia,hawaii,idaho,illinois,indiana,iowa,kansas,kentucky,louisiana,maine,maryland,massachusetts,michigan,minnesota,mississippi,missouri,montana,nebraska,nevada,new hampshire,new jersey,new mexico,new york,north carolina,north dakota,ohio,oklahoma,oregon,pennsylvania,rhode island,south carolina,south dakota,tennessee,texas,utah,vermont,virginia,washington,west virginia,wisconsin,wyoming,dc,washington dc'.split(','),
)
const US_COUNTRY = /^(usa|us|u\.s\.a?\.?|united states)$/i

/**
 * One-line location for cards (Veronika 2026-09-15): US states dropped so
 * "Tuscaloosa, Alabama, USA" → "Tuscaloosa, USA" and "Detroit, Michigan" → "Detroit, USA".
 */
export const displayLocation = (location?: string | null): string => {
  const parts = (location ?? '').split(',').map((s) => s.trim()).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length >= 3 && US_COUNTRY.test(parts[parts.length - 1]!)) return `${parts[0]}, USA`
  if (parts.length === 2 && (US_STATES.has(parts[1]!.toLowerCase()) || US_COUNTRY.test(parts[1]!))) return `${parts[0]}, USA`
  return parts.join(', ')
}

/**
 * Organiser link target for cards: the internal vendor page, but ONLY if that page is
 * published (`publishedVendorPaths`, from the vendors dump; e.g. Eirich is still a draft and
 * would 404), otherwise the external URL when the event carries one.
 */
export const organizerHrefOf = (ev: Event, publishedVendorPaths?: Set<string>): string | null => {
  if (!ev.organizerIsLinkable) return null
  if (ev.organizerLinkType === 'internal' && ev.organizerVendor && typeof ev.organizerVendor === 'object') {
    const v = ev.organizerVendor as { path?: string | null }
    if (v.path && (!publishedVendorPaths || publishedVendorPaths.has(v.path))) return v.path
    return null
  }
  if (ev.organizerLinkType === 'external' && ev.organizerLink) return ev.organizerLink
  return null
}

/** City part of a "City, Region, Country" location. */
export const cityOf = (location?: string | null): string =>
  (location ?? '').split(',')[0]?.trim() ?? ''

/**
 * Same conference series = same title with the year stripped. Used to point a past
 * event page at its next edition ("ICBR 2026" -> "ICBR 2027").
 */
export const seriesKeyOf = (title: string): string =>
  slugifyTitle(title.replace(/\b(19|20)\d{2}\b/g, '').replace(/\b\d+(st|nd|rd|th)\b/gi, ''))
